import { getState } from '/bb/state.js';
import {
  POINTER_IDLE_GAP_MS,
  POINTER_SAMPLE_MIN_DT_MS,
  POINTER_MIN_STEP_PX,
  HOVER_LATENCY_MAX_MS,
  INTERACTIVE_SELECTOR,
} from '/bb/constants.js';
import { welfordAdd } from '/bb/aggregators/welford.js';
import { trajectoryPush, trajectoryFlushSegment } from '/bb/aggregators/trajectory.js';
import { resolveField, isInteractive } from '/bb/identifier.js';
import { elementContains, ensureCurrentPage, getOrCreateField } from '/bb/helpers.js';

const hoverEntered = new WeakMap();
const RIGHT_CLICK_CONTEXTMENU_DEDUP_MS = 500;

let lastRightClick = null;

function bucketFor(pointerType) {
  const s = getState();
  return pointerType === 'touch' ? s.session.touch : s.session.pointer;
}

function pointerMoveHandler(e) {
  const bucket = bucketFor(e.pointerType || 'mouse');
  collectTouchShape(bucket, e);
  const t = Date.now();
  const x = e.clientX;
  const y = e.clientY;
  if (bucket.lastT === 0) {
    bucket.lastT = t;
    bucket.lastX = x;
    bucket.lastY = y;
    return;
  }
  const dt = t - bucket.lastT;
  if (dt < POINTER_SAMPLE_MIN_DT_MS) return;
  const dx = x - bucket.lastX;
  const dy = y - bucket.lastY;
  const dist = Math.hypot(dx, dy);
  if (dist < POINTER_MIN_STEP_PX) {
    bucket.lastT = t;
    return;
  }
  if (dt <= POINTER_IDLE_GAP_MS) {
    const v = dist / dt;
    bucket.sumDist += dist;
    bucket.sumTime += dt;
    bucket.sampleCount += 1;
    welfordAdd(bucket.speed, v);
    if (bucket.lastV > 0) {
      const a = Math.abs(v - bucket.lastV) / dt;
      welfordAdd(bucket.accel, a);
    }
    bucket.lastV = v;
    trajectoryPush(bucket.trajectory, x, y);
  } else {
    flushTrajectory(bucket);
    bucket.lastV = 0;
    trajectoryPush(bucket.trajectory, x, y);
  }
  bucket.lastT = t;
  bucket.lastX = x;
  bucket.lastY = y;
}

function pointerDownHandler(e) {
  const b = bucketFor(e.pointerType || 'mouse');
  collectTouchShape(b, e);
  const s = getState();
  s.transient.lastPointerDown = {
    target: e.target,
    t: Date.now(),
    button: e.button,
    x: e.clientX,
    y: e.clientY,
    pointerType: e.pointerType || 'mouse',
  };
  if (e.button === 2) recordRightClick(e, 'pointerdown');
}

function pointerUpHandler(e) {
  const b = bucketFor(e.pointerType || 'mouse');
  flushTrajectory(b);
  b.lastV = 0;
}

function flushTrajectory(bucket) {
  const summary = trajectoryFlushSegment(bucket.trajectory);
  if (summary && Number.isFinite(summary.deviationPx)) welfordAdd(bucket.pathDeviation, summary.deviationPx);
}

function clickHandler(e) {
  const b = bucketFor(e.pointerType || 'mouse');
  b.clicks.total += 1;
  if (isInteractive(e.target, INTERACTIVE_SELECTOR)) b.clicks.onInteractive += 1;
  const targetEl = e.target;
  if (targetEl && typeof targetEl.getBoundingClientRect === 'function') {
    let interactive = targetEl;
    if (targetEl.matches && !targetEl.matches(INTERACTIVE_SELECTOR) && targetEl.closest) {
      const c = targetEl.closest(INTERACTIVE_SELECTOR);
      if (c) interactive = c;
    }
    try {
      const r = interactive.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX || 0) - cx;
      const dy = (e.clientY || 0) - cy;
      const dPx = Math.hypot(dx, dy);
      const halfW = r.width / 2;
      const halfH = r.height / 2;
      const norm = Math.max(halfW, halfH) > 0 ? dPx / Math.max(halfW, halfH) : 0;
      welfordAdd(b.accuracyPx, dPx);
      welfordAdd(b.accuracyNorm, norm);
    } catch (err) {
      /* ignore */
    }
  }
  const enter = hoverEntered.get(e.target);
  if (enter && Number.isFinite(enter)) {
    const dt = Date.now() - enter;
    if (dt > 0 && dt < HOVER_LATENCY_MAX_MS) welfordAdd(b.latency, dt);
    hoverEntered.delete(e.target);
  }
}

function collectTouchShape(bucket, e) {
  if (e.pointerType !== 'touch') return;

  const pressure = Number(e.pressure);
  const width = Number(e.width);

  if (Number.isFinite(pressure)) welfordAdd(bucket.pressure, pressure);
  if (Number.isFinite(width) && width > 0) welfordAdd(bucket.width, width);
}

function recordRightClick(e, source) {
  const now = Date.now();
  const elapsedSinceLast = lastRightClick ? now - lastRightClick.t : Number.POSITIVE_INFINITY;
  const sameTarget =
    lastRightClick &&
    (elementContains(lastRightClick.target, e.target) || elementContains(e.target, lastRightClick.target));
  const duplicatesPointerDown =
    source === 'contextmenu' &&
    lastRightClick?.source === 'pointerdown' &&
    sameTarget &&
    elapsedSinceLast >= 0 &&
    elapsedSinceLast <= RIGHT_CLICK_CONTEXTMENU_DEDUP_MS;

  if (duplicatesPointerDown) {
    lastRightClick = null;
    return;
  }

  lastRightClick = { source, target: e.target, t: now };
  const b = bucketFor(source === 'pointerdown' ? e.pointerType || 'mouse' : 'mouse');
  b.rightClicks += 1;
  const field = resolveField(e.target);
  if (field) {
    const pageKey = ensureCurrentPage();
    const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
    if ('right_click_count' in fld) fld.right_click_count += 1;
  }
}

function contextMenuHandler(e) {
  recordRightClick(e, 'contextmenu');
}

function mouseOverHandler(e) {
  if (!e.target) return;
  const t = Date.now();
  hoverEntered.set(e.target, t);
  const field = resolveField(e.target);
  if (field) {
    const pageKey = ensureCurrentPage();
    const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
    if (fld.runtime.hoverSince === 0 && !fld.runtime.currentlyFocused) {
      fld.runtime.hoverSince = t;
    }
  }
}

function mouseOutHandler(e) {
  if (!e.target) return;
  const field = resolveField(e.target);
  if (field) {
    const pageKey = ensureCurrentPage();
    const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
    if (fld.runtime.hoverSince > 0 && !fld.runtime.currentlyFocused) {
      const t = Date.now();
      fld.runtime.hoverAccumMs += Math.max(0, t - fld.runtime.hoverSince);
      fld.runtime.hoverSince = 0;
    }
  }
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  lastRightClick = null;
  document.addEventListener('pointermove', pointerMoveHandler, { passive: true, capture: false });
  document.addEventListener('pointerdown', pointerDownHandler, { passive: true, capture: false });
  document.addEventListener('pointerup', pointerUpHandler, { passive: true, capture: false });
  document.addEventListener('click', clickHandler, { passive: true, capture: false });
  document.addEventListener('contextmenu', contextMenuHandler, { passive: true, capture: false });
  document.addEventListener('mouseover', mouseOverHandler, { passive: true, capture: false });
  document.addEventListener('mouseout', mouseOutHandler, { passive: true, capture: false });
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('pointermove', pointerMoveHandler);
  document.removeEventListener('pointerdown', pointerDownHandler);
  document.removeEventListener('pointerup', pointerUpHandler);
  document.removeEventListener('click', clickHandler);
  document.removeEventListener('contextmenu', contextMenuHandler);
  document.removeEventListener('mouseover', mouseOverHandler);
  document.removeEventListener('mouseout', mouseOutHandler);
  lastRightClick = null;
  attached = false;
}
